import { NgClass } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, linkedSignal, output, signal } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FileActionsService } from '@app/shared/services/actions/file-actions.service';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { TranslatePipe } from '@ngx-translate/core';
import { EmblaOptionsType } from 'embla-carousel';
import { finalize } from 'rxjs';
import { FileDescriptionComponent } from '../../files/file-description/file-description.component';
import { BaseEmblaCarouselComponent } from '../base-embla-carousel/base-embla-carousel.component';

@Component({
  selector: 'proffeo-full-image-carousel',
  templateUrl: './full-image-carousel.component.html',
  styleUrl: './full-image-carousel.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatIconModule, MatDialogModule, TranslatePipe, FileDescriptionComponent, NgClass, UiButtonComponent]
})
export class FullImageCarouselComponent extends BaseEmblaCarouselComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly fileActionsService = inject(FileActionsService);

  protected readonly isDownloading = signal(false);

  public readonly filesAdded = output<File[]>();
  public readonly fileDelete = output<FileInfo>();
  public readonly descriptionChanged = output<{ file: FileInfo; description: string }>();

  public readonly currentImage = linkedSignal(() => this.files()[this.index()]);

  protected override getEmblaOptions(): EmblaOptionsType {
    return {
      loop: false,
      align: 'center'
    };
  }

  protected toSafePdfUrl(rawUrl: string): SafeResourceUrl {
    const withParams = rawUrl.includes('#') ? rawUrl : `${rawUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(withParams);
  }

  protected downloadCurrentFile(): void {
    const current = this.currentImage();
    if (!current) return;

    this.isDownloading.set(true);
    this.fileActionsService
      .downloadFile(current)
      .pipe(finalize(() => this.isDownloading.set(false)))
      .subscribe();
  }

  protected onFileAdded(files: File[]): void {
    this.filesAdded.emit(files);
  }

  protected onFileDeleted(file: FileInfo): void {
    this.fileDelete.emit(file);
  }

  protected onDescriptionChange(description: string): void {
    const current = this.currentImage();
    if (!current) return;

    this.descriptionChanged.emit({
      file: current,
      description
    });
  }
}
